import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  getQuizCohortInjectionCandidates,
  pollQuizAiJob,
  postCohortQuiz,
  postQuizAiGenerate,
  postTeacherQuiz,
  type CoursePlanItem,
  type QuizAiDraft,
  type QuizCohortInjectionCandidate,
  type TeacherCohortAssignmentRecord,
  type TeacherQuizQuestion,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { AI_SPEND_TOOLTIP, AiSpendIcon } from "./ai-spend";
import LessonAiProgressPanel from "./LessonAiProgressPanel";
import { startLessonAiProgressClock, type LessonAiProgressUpdate } from "./lesson-ai-progress";
import { defaultQuizTitle, precedingLessonTitle, resolveQuizTitle } from "./quizDefaultTitle";
import { assignmentIdAfterPlanItem } from "./quizPlanInsert";

function newId(): string {
  return crypto.randomUUID();
}

function quizPoolSize(displayCount: number): number {
  return Math.ceil(displayCount * 1.5);
}

function blankStubQuestions(count: number): TeacherQuizQuestion[] {
  return Array.from({ length: count }, () => ({
    id: newId(),
    prompt: "",
    options: ["", "", "", ""],
    correctOptionIndex: 0,
  }));
}

type AddQuizContext = "cohort" | "template";
type ModalPhase = "form" | "ai";

export interface AddQuizModalProps {
  readonly open: boolean;
  readonly context: AddQuizContext;
  readonly courseId: string;
  readonly cohortId?: string;
  readonly insertAfterPlanItemId: string | null | undefined;
  readonly planItems: readonly CoursePlanItem[];
  readonly assignments?: readonly TeacherCohortAssignmentRecord[];
  readonly onClose: () => void;
  readonly onAdded: () => void | Promise<void>;
}

export function AddQuizModal({
  open,
  context,
  courseId,
  cohortId,
  insertAfterPlanItemId,
  planItems,
  assignments = [],
  onClose,
  onAdded,
}: AddQuizModalProps) {
  const navigate = useNavigate();
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const progressStopRef = useRef<(() => void) | null>(null);
  const cancelledRef = useRef(false);
  const [phase, setPhase] = useState<ModalPhase>("form");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [displayCount, setDisplayCount] = useState(5);
  const [guidance, setGuidance] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiProgress, setAiProgress] = useState<LessonAiProgressUpdate | null>(null);
  const [injectionLoading, setInjectionLoading] = useState(false);
  const [injectionCandidates, setInjectionCandidates] = useState<readonly QuizCohortInjectionCandidate[]>([]);
  const [injectionAnchorLesson, setInjectionAnchorLesson] = useState<{ id: string; title: string } | null>(
    null
  );
  const [selectedCohortIds, setSelectedCohortIds] = useState<ReadonlySet<string>>(() => new Set());

  const suggestedTitle = useMemo(
    () => defaultQuizTitle(planItems, insertAfterPlanItemId),
    [planItems, insertAfterPlanItemId]
  );
  const focusLessonName = useMemo(
    () => precedingLessonTitle(planItems, insertAfterPlanItemId),
    [planItems, insertAfterPlanItemId]
  );

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setPhase("form");
      setTitle("");
      setDescription("");
      setDisplayCount(5);
      setGuidance("");
      setError(null);
      setAiProgress(null);
      progressStopRef.current?.();
      progressStopRef.current = null;
      setInjectionLoading(false);
      setInjectionCandidates([]);
      setInjectionAnchorLesson(null);
      setSelectedCohortIds(new Set());
    }
  }, [open]);

  useEffect(() => {
    if (!open || context !== "template" || !bearer) {
      setInjectionCandidates([]);
      setInjectionAnchorLesson(null);
      setSelectedCohortIds(new Set());
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      setInjectionLoading(true);
      void getQuizCohortInjectionCandidates(bearer, courseId, insertAfterPlanItemId)
        .then((data) => {
          if (cancelled) return;
          setInjectionAnchorLesson(data.anchorLesson);
          setInjectionCandidates(data.candidates);
          setSelectedCohortIds(new Set(data.candidates.map((c) => c.cohortId)));
        })
        .catch(() => {
          if (cancelled) return;
          setInjectionAnchorLesson(null);
          setInjectionCandidates([]);
          setSelectedCohortIds(new Set());
        })
        .finally(() => {
          if (!cancelled) setInjectionLoading(false);
        });
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [bearer, context, courseId, insertAfterPlanItemId, open]);

  useEffect(() => {
    return () => {
      progressStopRef.current?.();
    };
  }, []);

  const insertAfterAssignmentId = useMemo(
    () => assignmentIdAfterPlanItem(insertAfterPlanItemId, planItems, assignments),
    [assignments, insertAfterPlanItemId, planItems]
  );

  const injectCohortIds = useMemo(() => {
    if (context !== "template" || selectedCohortIds.size === 0) return undefined;
    return [...selectedCohortIds];
  }, [context, selectedCohortIds]);

  const injectionAllSelected =
    injectionCandidates.length > 0 && selectedCohortIds.size === injectionCandidates.length;

  function toggleCohortInjection(cohortId: string, checked: boolean) {
    setSelectedCohortIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(cohortId);
      else next.delete(cohortId);
      return next;
    });
  }

  function toggleAllCohortInjection(checked: boolean) {
    if (!checked) {
      setSelectedCohortIds(new Set());
      return;
    }
    setSelectedCohortIds(new Set(injectionCandidates.map((c) => c.cohortId)));
  }

  const cancelGeneration = useCallback(() => {
    cancelledRef.current = true;
    progressStopRef.current?.();
    progressStopRef.current = null;
    setAiProgress(null);
    setBusy(false);
    setPhase("form");
  }, []);

  const handleBackdropClick = useCallback(
    (e: MouseEvent<HTMLDialogElement>) => {
      if (e.target === dialogRef.current && !busy) onClose();
    },
    [busy, onClose]
  );

  const finishCohortCreate = useCallback(async () => {
    await onAdded();
    onClose();
  }, [onAdded, onClose]);

  const finishCourseCreate = useCallback(
    async (quizId: string) => {
      await onAdded();
      onClose();
      navigate(`/teacher/courses/${encodeURIComponent(courseId)}/quizzes/${encodeURIComponent(quizId)}`);
    },
    [courseId, navigate, onAdded, onClose]
  );

  const quizPayload = useCallback(
    (resolvedTitle: string, questions: TeacherQuizQuestion[], creationPrompt?: string | null) => ({
      title: resolvedTitle,
      description: description.trim() || null,
      creationPrompt: creationPrompt ?? null,
      displayQuestionCount: displayCount,
      questions,
      iconName: "clipboard-question" as const,
      iconBgColor: "#1b4d2e",
      iconFgColor: "#fdf9f3",
    }),
    [description, displayCount]
  );

  async function handleCreateEmpty() {
    if (!bearer) return;
    const resolvedTitle = resolveQuizTitle(title, planItems, insertAfterPlanItemId);
    setBusy(true);
    setError(null);
    try {
      const pool = quizPoolSize(displayCount);
      const questions = blankStubQuestions(pool);
      if (context === "cohort" && cohortId) {
        await postCohortQuiz(bearer, cohortId, {
          ...quizPayload(resolvedTitle, questions),
          insertAfterAssignmentId,
        });
        await finishCohortCreate();
      } else {
        const { quiz } = await postTeacherQuiz(bearer, courseId, {
          ...quizPayload(resolvedTitle, questions),
          insertAfterPlanItemId,
          injectCohortIds,
        });
        await finishCourseCreate(quiz.id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create quiz");
    } finally {
      setBusy(false);
    }
  }

  async function handleGenerate() {
    if (!bearer) return;
    const resolvedTitle = resolveQuizTitle(title, planItems, insertAfterPlanItemId);
    setPhase("ai");
    setBusy(true);
    setError(null);
    progressStopRef.current?.();
    progressStopRef.current = startLessonAiProgressClock("quiz-generate", setAiProgress);
    cancelledRef.current = false;
    try {
      const trimmedGuidance = guidance.trim();
      const { jobId } = await postQuizAiGenerate(bearer, courseId, {
        prompt: trimmedGuidance,
        insertAfterPlanItemId,
      });
      const draft: QuizAiDraft = await pollQuizAiJob(bearer, courseId, jobId);
      if (cancelledRef.current) return;
      const questions = draft.questions.map((q) => ({
        id: newId(),
        prompt: q.prompt,
        options: [...q.options],
        correctOptionIndex: q.correctOptionIndex,
        ...(q.sourceLessonId ? { sourceLessonId: q.sourceLessonId } : {}),
        ...(q.topicTags ? { topicTags: [...q.topicTags] } : {}),
        ...(q.rationale ? { rationale: q.rationale } : {}),
        ...(q.optionExplanations ? { optionExplanations: [...q.optionExplanations] } : {}),
      }));
      const aiTitle = draft.title?.trim() || resolvedTitle;
      const aiPayload = {
        title: aiTitle,
        description: (draft.description ?? description.trim()) || null,
        creationPrompt: trimmedGuidance || null,
        displayQuestionCount: draft.displayQuestionCount ?? displayCount,
        questions,
        iconName: draft.iconName ?? "clipboard-question",
        iconBgColor: draft.iconBgColor ?? "#1b4d2e",
        iconFgColor: draft.iconFgColor ?? "#fdf9f3",
      };
      if (context === "cohort" && cohortId) {
        await postCohortQuiz(bearer, cohortId, {
          ...aiPayload,
          insertAfterAssignmentId,
        });
        if (cancelledRef.current) return;
        await finishCohortCreate();
        return;
      }
      const { quiz } = await postTeacherQuiz(bearer, courseId, {
        ...aiPayload,
        insertAfterPlanItemId,
        injectCohortIds,
      });
      if (cancelledRef.current) return;
      await finishCourseCreate(quiz.id);
    } catch (e) {
      if (cancelledRef.current) return;
      setError(e instanceof Error ? e.message : "Could not create quiz");
      setPhase("form");
    } finally {
      progressStopRef.current?.();
      progressStopRef.current = null;
      setAiProgress(null);
      setBusy(false);
    }
  }

  return (
    <dialog ref={dialogRef} className="teacher-modal" onClose={onClose} onClick={handleBackdropClick}>
      <form method="dialog" className="teacher-modal-card" role="document" onSubmit={(e) => e.preventDefault()}>
        <h2 className="teacher-modal-title">Add quiz</h2>
        <p className="teacher-modal-lede">
          {focusLessonName ? (
            <>
              AI will draft questions about <strong>{focusLessonName}</strong> (the lesson at this spot).
              Create an empty quiz to fill in yourself, or generate a draft with AI.
            </>
          ) : (
            <>
              Create an empty quiz to fill in yourself, or generate a draft with AI from lessons before this
              spot.
            </>
          )}
        </p>

        {phase === "ai" && aiProgress ? (
          <LessonAiProgressPanel mode="quiz-generate" progress={aiProgress} />
        ) : (
          <>
            <div className="teacher-field">
              <label htmlFor="add-quiz-title" className="teacher-field-label">
                Title (optional)
              </label>
              <input
                id="add-quiz-title"
                className="teacher-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={suggestedTitle}
                disabled={busy}
              />
              <p className="teacher-field-hint">
                Leave blank to use <strong>{suggestedTitle}</strong> based on the lesson before this spot.
              </p>
            </div>

            <div className="teacher-field">
              <label htmlFor="add-quiz-description" className="teacher-field-label">
                Description (optional)
              </label>
              <textarea
                id="add-quiz-description"
                className="teacher-input"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Short note for teachers"
                disabled={busy}
              />
            </div>

            <div className="teacher-field teacher-field--narrow">
              <label htmlFor="add-quiz-display-count" className="teacher-field-label">
                Questions per attempt
              </label>
              <input
                id="add-quiz-display-count"
                className="teacher-input"
                type="number"
                min={1}
                max={20}
                value={displayCount}
                onChange={(e) => setDisplayCount(Number.parseInt(e.target.value, 10) || 5)}
                disabled={busy}
              />
            </div>

            <div className="teacher-quiz-ai-section">
              <div className="teacher-field">
                <label htmlFor="add-quiz-guidance" className="teacher-field-label">
                  Additional guidance (optional)
                </label>
                <textarea
                  id="add-quiz-guidance"
                  className="teacher-input"
                  rows={3}
                  value={guidance}
                  onChange={(e) => setGuidance(e.target.value)}
                  placeholder="e.g. Focus on loops and emphasize common mistakes"
                  disabled={busy}
                />
              </div>
              <p className="muted small teacher-quiz-ai-hint">
                If you leave this blank, AI drafts questions from the lessons before this spot in your course plan.
                Add notes here only if you want to steer topics or emphasis.
              </p>
            </div>

            {context === "template" && injectionLoading ? (
              <p className="muted small teacher-quiz-injection-hint">Checking active cohorts…</p>
            ) : null}

            {context === "template" && !injectionLoading && injectionCandidates.length > 0 && injectionAnchorLesson ? (
              <div className="teacher-quiz-injection-panel" role="group" aria-labelledby="add-quiz-injection-title">
                <p id="add-quiz-injection-title" className="teacher-field-label">
                  Add to active cohorts
                </p>
                <p className="muted small teacher-quiz-injection-lede">
                  You have <strong>{injectionCandidates.length}</strong> active cohort
                  {injectionCandidates.length === 1 ? "" : "s"} where{" "}
                  <strong>{injectionAnchorLesson.title}</strong> is still open. Add this quiz to their lesson
                  plans too?
                </p>
                <label className="teacher-quiz-injection-row teacher-quiz-injection-row--master">
                  <input
                    type="checkbox"
                    checked={injectionAllSelected}
                    onChange={(e) => toggleAllCohortInjection(e.target.checked)}
                    disabled={busy}
                  />
                  <span>Select all</span>
                </label>
                <ul className="teacher-quiz-injection-list">
                  {injectionCandidates.map((candidate) => (
                    <li key={candidate.cohortId}>
                      <label className="teacher-quiz-injection-row">
                        <input
                          type="checkbox"
                          checked={selectedCohortIds.has(candidate.cohortId)}
                          onChange={(e) => toggleCohortInjection(candidate.cohortId, e.target.checked)}
                          disabled={busy}
                        />
                        <span>
                          {candidate.label}
                          <span className="muted small">
                            {" "}
                            ({candidate.anchorState === "assigned" ? "assigned" : "planned"})
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}

        {error ? (
          <div className="warn-banner" role="alert">
            {error}
          </div>
        ) : null}

        {phase === "ai" ? (
          <div className="teacher-modal-actions">
            <button type="button" className="btn secondary" onClick={cancelGeneration}>
              Cancel
            </button>
          </div>
        ) : null}

        {phase === "form" ? (
          <div className="teacher-modal-actions teacher-modal-actions--split">
            <button type="button" className="btn secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <div className="teacher-modal-actions-primary">
              <button
                type="button"
                className="btn secondary"
                disabled={busy}
                onClick={() => void handleCreateEmpty()}
              >
                {busy ? "Creating…" : "Create empty quiz"}
              </button>
              <button
                type="button"
                className="btn primary"
                disabled={busy}
                title={AI_SPEND_TOOLTIP}
                onClick={() => void handleGenerate()}
              >
                <AiSpendIcon /> Generate with AI
              </button>
            </div>
          </div>
        ) : null}
      </form>
    </dialog>
  );
}
